import {
  ENGAGEMENT_STATUSES,
  type EntitlementDataset,
  type EntitlementOverview,
  LICENCE_STATUSES,
  type Restriction,
  type SubscriptionEngagement,
  type SubscriptionOverview,
} from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  type Column,
  DataTable,
  EmptyState,
  LoadingRegion,
  PageHeader,
  Select,
  StatusBadge,
  Tabs,
  useDialog,
  useToast,
} from '@fruuit/ui'
import { Database, Handshake, UserMinus } from 'lucide-react'
import { useSearchParams } from 'react-router'
import { errorMessage } from '../../api/client'
import { formatDate } from '../../features/feeds/format'
import {
  useAddRestriction,
  useEntitlements,
  useGrantEntitlement,
  useGrantSubscription,
  useLiftRestriction,
  useRevokeEntitlement,
  useRevokeSubscription,
  useSubscriptions,
} from '../../features/subscriptions/api'
import { GrantHistory, GrantState } from '../../features/subscriptions/GrantHistory'
import { ReasonDialog } from '../../features/subscriptions/ReasonDialog'
import { RestrictionDialog } from '../../features/subscriptions/RestrictionDialog'
import { useTitle } from '../../lib/useTitle'

type EngagementAction = { mode: 'grant' | 'revoke'; engagement: SubscriptionEngagement }
type DatasetAction = { mode: 'grant' | 'revoke'; dataset: EntitlementDataset }

function EngagementsTab({ overview }: { overview: SubscriptionOverview }) {
  const { toast } = useToast()
  const dialog = useDialog<EngagementAction>()
  const grant = useGrantSubscription()
  const revoke = useRevokeSubscription()
  const action = dialog.data

  async function confirm(reason: string) {
    if (!action) return
    const { engagement } = action
    const result = await dialog.guard.run(() =>
      action.mode === 'grant'
        ? grant.mutateAsync({
            org_id: overview.org.id,
            engagement_id: engagement.engagement_id,
            reason,
          })
        : revoke.mutateAsync({
            id: engagement.subscription?.id ?? '',
            revision: engagement.subscription?.revision ?? 1,
            reason,
          }),
    )
    if (result.stale) return
    dialog.close()
    toast({
      tone: 'success',
      message:
        action.mode === 'grant'
          ? `${overview.org.name} is subscribed to “${engagement.title}”.`
          : `Subscription revoked. ${overview.org.name} no longer sees “${engagement.title}”.`,
    })
  }

  const columns: Column<SubscriptionEngagement>[] = [
    {
      id: 'case',
      header: 'Case',
      cell: (row) => (
        <div className="min-w-[14rem] max-w-md">
          <p className="font-semibold leading-snug text-ink">{row.title}</p>
          <p className="text-xs text-sage">
            {row.issuer_name}
            {row.theme ? ` · ${row.theme}` : ''}
          </p>
          {row.client_org_id ? (
            <p className="mt-1">
              <Badge tone="outline">
                {row.subscribable ? 'Client-specific case' : 'Another client’s case'}
              </Badge>
            </p>
          ) : null}
          <GrantHistory history={row.history} />
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Case status',
      cell: (row) => (
        <StatusBadge status={row.status} label={ENGAGEMENT_STATUSES.labels[row.status]} />
      ),
    },
    {
      id: 'access',
      header: 'Access',
      cell: (row) => (
        <GrantState
          grant={row.subscription}
          activeLabel="Subscribed"
          inactiveLabel="Not subscribed"
        />
      ),
    },
    {
      id: 'action',
      header: 'Action',
      align: 'right',
      cell: (row) =>
        row.subscription ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => dialog.open({ mode: 'revoke', engagement: row })}
            aria-label={`Revoke subscription to ${row.title}`}
          >
            Revoke
          </Button>
        ) : row.subscribable ? (
          <Button
            size="sm"
            onClick={() => dialog.open({ mode: 'grant', engagement: row })}
            aria-label={`Subscribe to ${row.title}`}
          >
            Subscribe
          </Button>
        ) : (
          <span className="text-xs text-sage">Not available</span>
        ),
    },
  ]

  return (
    <>
      <Callout tone="info" className="mb-5">
        Changes take effect on the investor’s next request. Revoking hides the case and the content
        that depends on it (objectives, milestones, meetings and minutes). It cannot recall anything
        already downloaded.
      </Callout>
      <DataTable
        caption={`Engagement subscriptions for ${overview.org.name}`}
        columns={columns}
        rows={overview.engagements}
        getRowId={(row) => row.engagement_id}
        pagination={{ mode: 'client', pageSize: 25 }}
        empty={<EmptyState variant="plain" icon={<Handshake />} title="No engagement cases yet" />}
      />
      {dialog.isOpen && action ? (
        <ReasonDialog
          open
          onClose={dialog.close}
          title={action.mode === 'grant' ? 'Subscribe to this case' : 'Revoke this subscription'}
          description={
            action.mode === 'grant'
              ? `${overview.org.name} will see the released content of “${action.engagement.title}” on its next request.`
              : `${overview.org.name} will stop seeing “${action.engagement.title}” and its dependent content on its next request.`
          }
          confirmLabel={action.mode === 'grant' ? 'Subscribe' : 'Revoke subscription'}
          confirmVariant={action.mode === 'grant' ? 'primary' : 'danger'}
          onConfirm={confirm}
        />
      ) : null}
    </>
  )
}

function RestrictionsTab({ overview }: { overview: SubscriptionOverview }) {
  const { toast } = useToast()
  const addDialog = useDialog()
  const liftDialog = useDialog<Restriction>()
  const add = useAddRestriction()
  const lift = useLiftRestriction()
  const target = liftDialog.data

  const columns: Column<Restriction>[] = [
    {
      id: 'user',
      header: 'User',
      cell: (row) => (
        <div>
          <p className="font-semibold text-ink">{row.user_name}</p>
          <p className="text-xs text-sage">{row.user_email}</p>
        </div>
      ),
    },
    {
      id: 'case',
      header: 'Case hidden from them',
      cell: (row) => <span className="text-ink">{row.engagement_title}</span>,
    },
    {
      id: 'reason',
      header: 'Reason',
      cell: (row) => (
        <div className="max-w-sm text-xs leading-relaxed text-ink-soft">
          <p>{row.reason}</p>
          <p className="mt-1 text-sage">Since {formatDate(row.created_at, '—')}</p>
        </div>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      align: 'right',
      cell: (row) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => liftDialog.open(row)}
          aria-label={`Lift restriction for ${row.user_name}`}
        >
          Lift
        </Button>
      ),
    },
  ]

  return (
    <>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm leading-relaxed text-sage">
          Optional per-user narrowing (D-04). The org keeps its subscription; a restricted user no
          longer sees that case.
        </p>
        <Button icon={<UserMinus />} onClick={() => addDialog.open()}>
          Restrict a user
        </Button>
      </div>
      <DataTable
        caption={`Per-user restrictions in ${overview.org.name}`}
        columns={columns}
        rows={overview.restrictions}
        getRowId={(row) => row.id}
        empty={
          <EmptyState
            variant="plain"
            title="No restrictions"
            description="Every user in this org sees every case the org subscribes to."
          />
        }
      />
      {addDialog.isOpen ? (
        <RestrictionDialog
          overview={overview}
          onClose={addDialog.close}
          onConfirm={async (input) => {
            const result = await addDialog.guard.run(() =>
              add.mutateAsync({ org_id: overview.org.id, ...input }),
            )
            if (result.stale) return
            addDialog.close()
            toast({ tone: 'success', message: 'Restriction added.' })
          }}
        />
      ) : null}
      {liftDialog.isOpen && target ? (
        <ReasonDialog
          open
          onClose={liftDialog.close}
          title="Lift this restriction"
          description={`${target.user_name} will see “${target.engagement_title}” again on their next request.`}
          confirmLabel="Lift restriction"
          onConfirm={async (reason) => {
            const result = await liftDialog.guard.run(() =>
              lift.mutateAsync({ id: target.id, reason }),
            )
            if (result.stale) return
            liftDialog.close()
            toast({ tone: 'success', message: 'Restriction lifted.' })
          }}
        />
      ) : null}
    </>
  )
}

function DatasetsTab({ entitlements }: { entitlements: EntitlementOverview }) {
  const { toast } = useToast()
  const dialog = useDialog<DatasetAction>()
  const grant = useGrantEntitlement()
  const revoke = useRevokeEntitlement()
  const action = dialog.data
  const org = entitlements.org

  async function confirm(reason: string) {
    if (!action) return
    const { dataset } = action
    const result = await dialog.guard.run(() =>
      action.mode === 'grant'
        ? grant.mutateAsync({ org_id: org.id, dataset: dataset.dataset, reason })
        : revoke.mutateAsync({
            id: dataset.entitlement?.id ?? '',
            revision: dataset.entitlement?.revision ?? 1,
            reason,
          }),
    )
    if (result.stale) return
    dialog.close()
    toast({
      tone: 'success',
      message:
        action.mode === 'grant'
          ? `${org.name} is entitled to ${dataset.label}.`
          : `${dataset.label} entitlement revoked for ${org.name}.`,
    })
  }

  const columns: Column<EntitlementDataset>[] = [
    {
      id: 'dataset',
      header: 'Dataset',
      cell: (row) => (
        <div className="min-w-[14rem]">
          <p className="font-semibold text-ink">{row.label}</p>
          <p className="text-xs text-sage">{row.provider_name}</p>
          <p className="mt-1.5 flex flex-wrap gap-1.5">
            {row.licence_status !== 'simulated' ? (
              <StatusBadge
                status={row.licence_status === 'pending' ? 'licence pending' : row.licence_status}
                label={LICENCE_STATUSES.labels[row.licence_status]}
              />
            ) : null}
          </p>
          <GrantHistory history={row.history} />
        </div>
      ),
    },
    {
      id: 'access',
      header: 'Access',
      cell: (row) => (
        <GrantState grant={row.entitlement} activeLabel="Entitled" inactiveLabel="Not entitled" />
      ),
    },
    {
      id: 'action',
      header: 'Action',
      align: 'right',
      cell: (row) =>
        row.entitlement ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => dialog.open({ mode: 'revoke', dataset: row })}
            aria-label={`Revoke ${row.label}`}
          >
            Revoke
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => dialog.open({ mode: 'grant', dataset: row })}
            aria-label={`Grant ${row.label}`}
          >
            Grant
          </Button>
        ),
    },
  ]

  return (
    <>
      <Callout tone="info" className="mb-5">
        Dataset entitlements decide which orgs see feed-derived fields (P-CAN-003). New published
        versions in an entitled dataset become visible automatically; feed signals stay internal
        until Fruuit staff review them.
      </Callout>
      <DataTable
        caption={`Dataset entitlements for ${org.name}`}
        columns={columns}
        rows={entitlements.datasets}
        getRowId={(row) => row.dataset}
        empty={<EmptyState variant="plain" icon={<Database />} title="No datasets configured" />}
      />
      {dialog.isOpen && action ? (
        <ReasonDialog
          open
          onClose={dialog.close}
          title={action.mode === 'grant' ? 'Grant dataset access' : 'Revoke dataset access'}
          description={
            action.mode === 'grant'
              ? `${org.name} will see ${action.dataset.label} fields on its next request.`
              : `${org.name} will stop seeing ${action.dataset.label} fields on its next request.`
          }
          confirmLabel={action.mode === 'grant' ? 'Grant access' : 'Revoke access'}
          confirmVariant={action.mode === 'grant' ? 'primary' : 'danger'}
          onConfirm={confirm}
        />
      ) : null}
    </>
  )
}

/** M-02 / M-05 Subscriptions & entitlements (P-ID-004, P-ADM-010, P-CAN-005). */
export function SubscriptionsPage() {
  useTitle('Subscriptions', 'Admin')
  const [params, setParams] = useSearchParams()
  const requestedOrg = params.get('org')
  const subscriptions = useSubscriptions(requestedOrg)
  const orgId = requestedOrg ?? subscriptions.data?.org.id ?? null
  const entitlements = useEntitlements(orgId)
  const overview = subscriptions.data
  const tab = params.get('tab') ?? 'engagements'

  function setParam(key: string, value: string) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.set(key, value)
        return next
      },
      { replace: true },
    )
  }

  return (
    <>
      <PageHeader
        eyebrow="Access"
        title="Subscriptions & entitlements"
        description="Which investor orgs see which engagement cases and which data feeds. Every change needs a reason and is recorded in the platform and org audit trails."
        actions={
          overview ? (
            <Select
              label="Investor org"
              className="w-72 max-w-full"
              options={overview.orgs.map((org) => ({
                value: org.id,
                label: org.status === 'suspended' ? `${org.name} (suspended)` : org.name,
              }))}
              value={overview.org.id}
              onChange={(event) => setParam('org', event.target.value)}
            />
          ) : null
        }
      />
      {subscriptions.isPending ? (
        <LoadingRegion label="Loading subscriptions" />
      ) : subscriptions.isError ? (
        <Callout tone="danger" role="alert">
          {errorMessage(subscriptions.error)}
        </Callout>
      ) : overview ? (
        <Tabs
          label="Access type"
          value={tab}
          onValueChange={(id) => setParam('tab', id)}
          items={[
            {
              id: 'engagements',
              label: 'Engagements',
              count: overview.engagements.filter((e) => e.subscription).length,
              content: <EngagementsTab key={overview.org.id} overview={overview} />,
            },
            {
              id: 'restrictions',
              label: 'User restrictions',
              count: overview.restrictions.length,
              content: <RestrictionsTab key={overview.org.id} overview={overview} />,
            },
            {
              id: 'datasets',
              label: 'Datasets',
              count: entitlements.data?.datasets.filter((d) => d.entitlement).length,
              content: entitlements.isError ? (
                <Callout tone="danger" role="alert">
                  {errorMessage(entitlements.error)}
                </Callout>
              ) : entitlements.data ? (
                <DatasetsTab key={entitlements.data.org.id} entitlements={entitlements.data} />
              ) : (
                <LoadingRegion label="Loading dataset entitlements" />
              ),
            },
          ]}
        />
      ) : null}
    </>
  )
}
